/**
 * Filtros de Torre de Control.
 *
 * Cada vista tiene su propio panel, pero el payload que viaja al backend es uno
 * solo. Aquí se define la forma canónica, la serialización al request y la
 * conversión desde/hacia un filtro guardado ("Mis Filtros").
 */

/**
 * Los catálogos guardan la CLAVE de traducción, no el texto: el texto visible
 * siempre sale de `$t()` en el componente.
 */
export const ORIGIN_OPTIONS = [
  { value: 'form', tKey: 'controlTower.origin_form' },
  { value: 'task', tKey: 'controlTower.origin_task' },
  { value: 'quick_review', tKey: 'controlTower.origin_quick_review' },
  { value: 'sprint', tKey: 'controlTower.origin_sprint' },
  { value: 'resource', tKey: 'controlTower.origin_resource' },
  { value: 'routine', tKey: 'controlTower.origin_routine' }
]

export const PRIORITY_OPTIONS = [
  { value: 1, tKey: 'controlTower.priority_low' },
  { value: 2, tKey: 'controlTower.priority_medium' },
  { value: 3, tKey: 'controlTower.priority_high' }
]

export const STATE_OPTIONS = [
  { value: 'overdue', tKey: 'controlTower.overdue' },
  { value: 'in_progress', tKey: 'controlTower.in_progress' },
  { value: 'finished', tKey: 'controlTower.finished' },
  { value: 'suspended', tKey: 'controlTower.suspended' },
  { value: 'escalated', tKey: 'controlTower.escalated' }
]

export const SORT_OPTIONS = [
  { value: 'priority', tKey: 'controlTower.priority' },
  { value: 'end_date', tKey: 'controlTower.sort_end_date' },
  { value: 'title', tKey: 'controlTower.sort_title' },
  { value: 'assignee', tKey: 'controlTower.responsible' },
  { value: 'location', tKey: 'controlTower.location' },
  { value: 'state', tKey: 'controlTower.status' }
]

/**
 * Traduce un catálogo con la función `$t` del componente.
 */
export function translateOptions (options, t) {
  return options.map(option => ({ value: option.value, label: t(option.tKey) }))
}

const pad = value => String(value).padStart(2, '0')

export function toIsoDate (value) {
  const parsed = value instanceof Date ? value : new Date(value)
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
}

/**
 * Rango por defecto: el mismo que usaba el backend cuando no le mandaban fechas
 * (hoy - 7 días … hoy + 2 meses). Sin dependencia de Quasar, para que la util
 * se pueda probar sin arrancar el framework.
 */
export function defaultDateRange (today = new Date()) {
  const start = new Date(today.getTime())
  start.setDate(start.getDate() - 7)

  const end = new Date(today.getTime())
  end.setMonth(end.getMonth() + 2)

  return { startDate: toIsoDate(start), endDate: toIsoDate(end) }
}

export function emptyFilters () {
  const range = defaultDateRange()

  return {
    startDate: range.startDate,
    endDate: range.endDate,
    locations: [],
    users: [],
    states: [],
    priorities: [],
    origins: [],
    sites: [],
    subcategories: [],
    areas: [],
    categorie: null,
    area: null,
    search: null,
    sort: ['end_date'],
    // Dirección por llave de `sort` (asc por defecto). Va aparte para que los
    // filtros guardados antes de existir el modal de ordenamiento sigan válidos.
    sortDirs: {}
  }
}

/**
 * ¿Hay algo puesto además del rango de fechas por defecto? Es lo que decide si
 * el "+" de Mis Filtros se pinta gris o rojo.
 */
export function hasActiveFilters (filters) {
  if (!filters) return false

  const listKeys = ['locations', 'users', 'states', 'priorities', 'origins', 'sites', 'subcategories', 'areas']
  if (listKeys.some(key => Array.isArray(filters[key]) && filters[key].length > 0)) return true

  return Boolean(filters.categorie || filters.area || filters.search)
}

export function countActiveFacets (filters) {
  if (!filters) return 0

  const listKeys = ['locations', 'users', 'states', 'priorities', 'origins', 'sites', 'subcategories', 'areas']
  const fromLists = listKeys.reduce((total, key) => total + (Array.isArray(filters[key]) ? filters[key].length : 0), 0)
  const fromScalars = ['categorie', 'area', 'search'].filter(key => Boolean(filters[key])).length

  return fromLists + fromScalars
}

/**
 * Payload del request. Se omite lo vacío para no mandar ruido.
 */
export function toRequestPayload (filters, extra = {}) {
  const payload = { ...extra }
  const source = filters || emptyFilters()

  payload.startDate = source.startDate
  payload.endDate = source.endDate

  const listKeys = ['locations', 'users', 'states', 'priorities', 'origins', 'sites', 'subcategories', 'areas']
  listKeys.forEach(key => {
    if (Array.isArray(source[key]) && source[key].length) payload[key] = source[key]
  })

  if (source.categorie) payload.categorie = source.categorie
  if (source.area) payload.area = source.area
  if (source.search) payload.search = source.search

  const sort = sortSpec(source.sort, source.sortDirs)
  if (sort.length) payload.sort = sort

  return payload
}

/**
 * Lo que se guarda en "Mis Filtros". `locations` viaja además en la columna
 * legada `locations_id` para no romper los filtros ya guardados.
 */
export function toSavedFilterPayload (filters, { name, viewType, overwrite = false } = {}) {
  const source = filters || emptyFilters()

  return {
    name,
    view_type: viewType,
    overwrite,
    locations: source.locations,
    categorie: source.categorie,
    area: source.area,
    payload: {
      startDate: source.startDate,
      endDate: source.endDate,
      users: source.users,
      states: source.states,
      priorities: source.priorities,
      origins: source.origins,
      sites: source.sites,
      subcategories: source.subcategories,
      areas: source.areas,
      search: source.search,
      sort: source.sort,
      sortDirs: source.sortDirs || {}
    }
  }
}

/**
 * Reconstruye los filtros desde un registro guardado. Soporta los filtros
 * viejos, que solo tienen `locations_id`, `categorie` y `area`.
 */
export function fromSavedFilter (record) {
  const base = emptyFilters()
  if (!record) return base

  const payload = record.payload || {}

  const legacyLocations = typeof record.locations_id === 'string' && record.locations_id.length
    ? record.locations_id.split(',').map(id => parseInt(id, 10)).filter(id => !Number.isNaN(id))
    : []

  return {
    ...base,
    startDate: payload.startDate || base.startDate,
    endDate: payload.endDate || base.endDate,
    locations: legacyLocations,
    users: payload.users || [],
    states: payload.states || [],
    priorities: payload.priorities || [],
    origins: payload.origins || [],
    sites: payload.sites || [],
    subcategories: payload.subcategories || [],
    areas: payload.areas || [],
    categorie: record.categorie || null,
    area: record.area || null,
    search: payload.search || null,
    sort: payload.sort && payload.sort.length ? payload.sort : base.sort,
    sortDirs: payload.sortDirs || {}
  }
}

/**
 * Ordenamiento: la lista de llaves donde la primera es la de mayor prioridad.
 */
export function moveSortKey (sort, from, to) {
  const next = (sort || []).slice()
  if (from < 0 || to < 0 || from >= next.length || to >= next.length) return next

  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)

  return next
}

export function removeSortKey (sort, key) {
  return (sort || []).filter(item => item !== key)
}

export function addSortKey (sort, key) {
  const next = (sort || []).slice()
  if (!next.includes(key)) next.push(key)
  return next
}

/**
 * Ordenamiento tal como viaja al backend: [{ key, dir }] en orden de prioridad.
 */
export function sortSpec (sort, sortDirs) {
  return (sort || []).map(key => ({
    key,
    dir: sortDirs && sortDirs[key] === 'desc' ? 'desc' : 'asc'
  }))
}

export function primarySort (sort) {
  return (sort || [])[0] || 'end_date'
}

/**
 * Estatus HTTP de un error de axios en esta app.
 *
 * `api/responser.js` (el interceptor de respuesta) rechaza con
 * `error.response` — la respuesta plana — y no con el `AxiosError`, así que
 * `error.response.status` es `undefined` y cualquier `if (status === 409)`
 * escrito de la forma habitual nunca entra. Se contemplan las dos formas para
 * no depender de por dónde venga el error.
 */
export function httpStatusOf (error) {
  if (!error) return null
  if (typeof error.status === 'number') return error.status
  if (error.response && typeof error.response.status === 'number') return error.response.status
  return null
}

/**
 * Cuerpo de la respuesta de error, con la misma salvedad.
 */
export function httpDataOf (error) {
  if (!error) return null
  return error.data || (error.response && error.response.data) || null
}
